import { Table } from '@tanstack/react-table';
import React, { useEffect, useState } from 'react';
import { useContext } from 'react';
import { FilterSettings, IFilter } from '../Table.interfaces';
import { v4 as uuidv4 } from 'uuid';
import { useTableViewStateContext } from '../TableViewStateContextProvider';

type TableContextReturn<T> = {
	table: Table<T>;
	filterState?: FilterSettings<T>;
	toggleFilter: (toggledFilter: IFilter<T>) => void;
	tableId?: string;
};

type TableContextProviderProps<T> = {
	table: Table<T>;
	filterSettings?: FilterSettings<T>;
	data: T[];
	tableId?: string;
};

const TableContext = React.createContext<TableContextReturn<any> | null>(null);

export function TableContextProvider<T>({
	children,
	table,
	filterSettings,
	data,
	tableId,
}: React.PropsWithChildren<TableContextProviderProps<T>>) {
	const { getViewState, updateViewState } = useTableViewStateContext<T>();

	const getInitialFilterState = (): FilterSettings<T> => {
		const defaultFilterSettings: FilterSettings<T> = {
			treatGroupsAsSingle: false,
			groups: [],
		};

		if (tableId) {
			return (
				getViewState(tableId)?.filters ||
				filterSettings ||
				defaultFilterSettings
			);
		}

		return filterSettings || defaultFilterSettings;
	};

	const [filterState, setFilterState] = useState<FilterSettings<T>>(
		getInitialFilterState(),
	);

	useEffect(() => {
		applyFilters();
	}, [filterState]);

	useEffect(() => {
		updateCounters();
	}, [data]);

	const updateCounters = () => {
		const updatedFilterGroups = filterState.groups.map((filterGroup) => {
			const updatedFilters = filterGroup.filters?.map((filter) => {
				if (filter.showCount) {
					const count =
						typeof filter !== 'undefined' && filter.countFn
							? filter.countFn(data)
							: 0;

					return {
						...filter,
						count,
					};
				}

				return filter;
			});

			return {
				...filterGroup,
				filters: updatedFilters,
			};
		});

		setFilterState({
			...filterState,
			groups: updatedFilterGroups,
		});
	};

	const applyFilters = () => {
		table.resetColumnFilters();

		filterState.groups.forEach((filterGroup) => {
			filterGroup.filters?.forEach((filter) => {
				if (filter.selected) {
					const column = table.getColumn(filterGroup.columnId);
					column?.setFilterValue(filter.value);
				}

				if (!filter.id) {
					filter.id = uuidv4();
				}
			});
		});
	};

	const toggleFilter = (toggledFilter: IFilter<T>) => {
		const updatedFilterGroups = filterState.groups.map((filterGroup) => {
			const toggledFilterIsInGroup = filterGroup.filters?.find(
				(f) => f.id === toggledFilter.id,
			);

			if (filterSettings?.treatGroupsAsSingle || toggledFilterIsInGroup) {
				const updatedFilters = filterGroup.filters?.map((filter) =>
					filter.id === toggledFilter.id
						? {
								...filter,
								selected: !filter.selected,
						  }
						: {
								...filter,
								selected: false,
						  },
				);

				return {
					...filterGroup,
					filters: updatedFilters,
				};
			}

			return filterGroup;
		});

		setFilterState({
			...filterState,
			groups: updatedFilterGroups,
		});

		updateViewState(tableId, {
			filters: {
				...filterState,
				groups: updatedFilterGroups,
			},
		});
	};

	return (
		<TableContext.Provider
			value={{
				table,
				filterState,
				toggleFilter,
				tableId,
			}}
		>
			{children}
		</TableContext.Provider>
	);
}

export function useTableContext<T>() {
	const context = useContext<TableContextReturn<T>>(
		TableContext as unknown as React.Context<TableContextReturn<T>>,
	);
	if (!context) {
		throw new Error(
			'useTableContext must be used under TableContextProvider',
		);
	}
	return context;
}
